import React, { useState } from 'react';
import { Box, Button, Typography, Paper, useTheme, Divider } from '@mui/material';
import { Stop, Refresh } from '@mui/icons-material';
import Layout from '../../components/Layout';
import LiveMeetingForm from './LiveMeetingForm';
import Stopwatch from '../../components/Stopwatch';
import LiveCostCalculator from './LiveCostCalculator';
import ShareButton from '../../components/ShareButton';
import { formatGBP } from '../../utils/formatGBP';
import { rainbowShareButtonStyles } from '../../theme/shareButtonStyles';

export default function LiveMeetingPage() {
  const theme = useTheme();
  const [attendees, setAttendees] = useState({});
  const [meetingName, setMeetingName] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const [meetingEnded, setMeetingEnded] = useState(false);
  const [finalCost, setFinalCost] = useState(0);

  const handleStart = (selectedAttendees, name = null) => {
    setAttendees(selectedAttendees);
    setMeetingName(name);
    setMeetingStarted(true);
    setIsRunning(true);
    setElapsedSeconds(0);
    setMeetingEnded(false);
  };

  const handleStop = () => {
    setIsRunning(false);
    setMeetingEnded(true);
    
    // Calculate final cost
    const totalHourlyRate = Object.keys(attendees).reduce((total, roleKey) => {
      const role = require('../../data/roles').roles.find(r => r.key === roleKey);
      const count = attendees[roleKey] || 0;
      return total + (role ? role.hourlyRate * count : 0);
    }, 0);
    
    const hours = elapsedSeconds / 3600;
    setFinalCost(totalHourlyRate * hours);
  };

  const handleNewMeeting = () => {
    setAttendees({});
    setMeetingName(null);
    setMeetingStarted(false);
    setIsRunning(false);
    setElapsedSeconds(0);
    setMeetingEnded(false);
    setFinalCost(0);
  };

  const handleTick = (seconds) => {
    setElapsedSeconds(seconds);
  };

  if (!meetingStarted) {
    return (
      <Layout variant="calculator">
        <LiveMeetingForm
          attendees={attendees}
          onAttendeesChange={setAttendees}
          onStart={handleStart}
        />
      </Layout>
    );
  }

  if (meetingEnded) {
    return (
      <Layout variant="calculator">
        <Paper 
          elevation={6} 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              mb: 5,
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 800,
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
            }}
          >
{meetingName ? `🏁 ${meetingName}` : '🏁 Meeting Complete'}
          </Typography>
          
          {/* Cost Display */}
          <Box 
            sx={{ 
              mb: 5,
              p: 4,
              borderRadius: 4,
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(25, 118, 210, 0.04)',
              border: theme.palette.mode === 'dark' 
                ? '2px solid rgba(255,255,255,0.1)'
                : '2px solid rgba(25, 118, 210, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                mb: 2,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              Total Cost
            </Typography>
            <Typography 
              variant="h1" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                mb: 0,
                fontFamily: 'monospace',
                fontWeight: 'bold',
                fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
                letterSpacing: 2,
              }}
            >
              {formatGBP(finalCost)}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4, opacity: 0.3 }} />

          {/* Meeting Details */}
          <Box 
            sx={{ 
              mb: 5,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3,
            }}
          >
            <Box 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.03)'
                  : 'action.hover',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.05)'
                  : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                ⏱️ Duration
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                }}
              >
                {Math.floor(elapsedSeconds / 3600)}h {Math.floor((elapsedSeconds % 3600) / 60)}m {elapsedSeconds % 60}s
              </Typography>
            </Box>

            <Box 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.03)'
                  : 'action.hover',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.05)'
                  : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 2,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                👥 Attendees
              </Typography>
              {Object.entries(attendees)
                .filter(([, count]) => count > 0)
                .map(([roleKey, count]) => {
                  const role = require('../../data/roles').roles.find(r => r.key === roleKey);
                  return (
                    <Box key={roleKey} sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                          fontWeight: 500,
                          color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                        }}
                      >
                        {role?.label || roleKey}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontWeight: 'bold',
                          color: theme.palette.mode === 'dark' ? '#66bb6a' : 'primary.main',
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  );
                })}
              <Box sx={{ mt: 2, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                    textAlign: 'center',
                  }}
                >
                  Total: {Object.values(attendees).reduce((sum, count) => sum + count, 0)} people
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <ShareButton
              meetingName={meetingName}
              attendees={attendees}
              elapsedSeconds={elapsedSeconds}
              finalCost={finalCost}
              variant="contained"
              size="large"
              sx={rainbowShareButtonStyles}
            />
            <Button
              variant="contained"
              size="large"
              onClick={handleNewMeeting}
              startIcon={<Refresh />}
              sx={{ 
                px: 6, 
                py: 3, 
                fontSize: '1.2rem',
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 700,
                borderRadius: 3,
                background: 'linear-gradient(45deg, #51cf66 30%, #69db7c 90%)',
                boxShadow: '0 4px 12px rgba(81, 207, 102, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  background: 'linear-gradient(45deg, #40c057 30%, #51cf66 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(81, 207, 102, 0.4)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              }}
            >
              Start New Meeting
            </Button>
          </Box>
        </Paper>
      </Layout>
    );
  }

  return (
    <Layout variant="calculator">
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            mb: 4,
            textAlign: 'center',
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 700,
          }}
        >
          {meetingName || 'Meeting in Progress'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
          {/* Stopwatch */}
          <Box sx={{ textAlign: 'center' }}>
            <Stopwatch 
              isRunning={isRunning} 
              elapsedSeconds={elapsedSeconds}
              onTick={handleTick}
            />
          </Box>

          {/* Live Cost */}
          <Box sx={{ textAlign: 'center' }}>
            <LiveCostCalculator 
              attendees={attendees}
              elapsedSeconds={elapsedSeconds}
            />
          </Box>

          {/* Stop Button */}
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={handleStop}
            startIcon={<Stop />}
            sx={{ 
              px: 5, 
              py: 2, 
              fontSize: '1.25rem',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 900
            }}
          >
            Stop Meeting
          </Button>
        </Box>
      </Paper>
    </Layout>
  );
}